import { useMemo, useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  computeRocketNozzleOutputs,
  fmt,
} from '@/lib/physics/rocketNozzleOutputs';
import { TubeframeForm } from './parameterArea/TubeframeForm';
import { RocketNozzleForm } from './parameterArea/RocketNozzleForm';
import { ft2, defaultInputs } from './parameterArea/rocketNozzleFields';
import type {
  ParameterAreaProps,
  InputKey,
  InputState,
} from './parameterArea/types';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ParameterArea({
  enabled,
  onEnabledChange,
  activeTab,
  onTabChange,
  onParamsReadyChange,
  onTubeframeParamsChange,
}: ParameterAreaProps) {
  const [inputs, setInputs] = useState<InputState>(defaultInputs);

  const [tubeUnits, setTubeUnits] = useState<'in' | 'mm'>('in');
  const [tubeOD, setTubeOD] = useState('');
  const [tubeID, setTubeID] = useState('');
  const [tubeJointAngle, setTubeJointAngle] = useState('');
  const [tubeMatingSurface, setTubeMatingSurface] = useState('');

  useEffect(() => {
    const ready =
      activeTab === 'land' &&
      tubeOD.trim() !== '' &&
      tubeID.trim() !== '' &&
      tubeJointAngle.trim() !== '' &&
      tubeMatingSurface !== '';
    onParamsReadyChange?.(ready);
    onTubeframeParamsChange?.(
      ready
        ? {
            od: tubeOD.trim(),
            id: tubeID.trim(),
            angle: tubeJointAngle.trim(),
            matingSurface: tubeMatingSurface,
            units: tubeUnits,
          }
        : null,
    );
  }, [
    activeTab,
    tubeOD,
    tubeID,
    tubeJointAngle,
    tubeMatingSurface,
    tubeUnits,
    onParamsReadyChange,
    onTubeframeParamsChange,
  ]);

  const outputs = useMemo(() => computeRocketNozzleOutputs(inputs), [inputs]);

  const setInput = (key: InputKey, v: string) =>
    setInputs((prev) => ({ ...prev, [key]: v }));

  const outputFields: Array<{
    label: React.ReactNode;
    unit: React.ReactNode;
    value: string;
  }> = [
    {
      label: (
        <>
          {'Throat Mach Number (M'}
          <sub>{'t'}</sub>
          {')'}
        </>
      ),
      unit: '[-]',
      value: outputs ? fmt(outputs.Mt) : '—',
    },
    {
      label: (
        <>
          {'Exit Mach Number (M'}
          <sub>{'e'}</sub>
          {')'}
        </>
      ),
      unit: '[-]',
      value: outputs ? fmt(outputs.Me) : '—',
    },
    {
      label: (
        <>
          {'Exit Pressure (P'}
          <sub>{'e'}</sub>
          {')'}
        </>
      ),
      unit: '[PSI]',
      value: outputs ? fmt(outputs.Pe_psi) : '—',
    },
    {
      label: (
        <>
          {'Exit Velocity (U'}
          <sub>{'e'}</sub>
          {')'}
        </>
      ),
      unit: '[FPS]',
      value: outputs ? fmt(outputs.Ue_fps) : '—',
    },
    {
      label: 'Mass Flow Rate',
      unit: '[lb./s]',
      value: outputs ? fmt(outputs.mdot_lbps) : '—',
    },
    {
      label: 'Thrust',
      unit: '[lbs]',
      value: outputs ? fmt(outputs.F_lbs) : '—',
    },
    {
      label: (
        <>
          {'Specific Impulse (I'}
          <sub>{'sp'}</sub>
          {')'}
        </>
      ),
      unit: '[s]',
      value: outputs ? fmt(outputs.Isp_s) : '—',
    },
    {
      label: 'Nozzle Pressure Ratio (NPR)',
      unit: '[-]',
      value: outputs ? fmt(outputs.NPR) : '—',
    },
    {
      label: (
        <>
          {'Characteristic Velocity (c*'}
          {')'}
        </>
      ),
      unit: '[FPS]',
      value: outputs ? fmt(outputs.cstar_fps) : '—',
    },
    {
      label: (
        <>
          {'Thrust Coefficient (C'}
          <sub>{'F'}</sub>
          {')'}
        </>
      ),
      unit: '[-]',
      value: outputs ? fmt(outputs.CF) : '—',
    },
    {
      label: (
        <>
          {'Exit Area (A'}
          <sub>{'e'}</sub>
          {')'}
        </>
      ),
      unit: ft2,
      value: outputs ? fmt(outputs.Ae_ft2) : '—',
    },
  ];

  return (
    <div
      className={cn(
        'w-full rounded-2xl border border-telekinesis-neutral-700 bg-telekinesis-bg-dark p-4 transition-opacity duration-200',
        !enabled && 'opacity-40',
      )}
    >
      <div className="mb-3 flex items-center gap-2">
        <Switch checked={enabled} onCheckedChange={onEnabledChange} />
        <p className="text-sm font-semibold text-telekinesis-text-primary">
          Vehicle Prompt Parameters
        </p>
      </div>
      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList className="mb-3 flex flex-wrap gap-1 bg-transparent p-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <TabsTrigger value="sea" disabled>
                  Sea
                </TabsTrigger>
              </span>
            </TooltipTrigger>
            <TooltipContent>Coming soon.</TooltipContent>
          </Tooltip>
          <TabsTrigger value="air" disabled={!enabled}>
            Air
          </TabsTrigger>
          <TabsTrigger value="land" disabled={!enabled}>
            Land
          </TabsTrigger>
          <TabsTrigger value="space" disabled={!enabled}>
            Space
          </TabsTrigger>
        </TabsList>

        {/*
          Grid overlay: forceMount keeps all panels in DOM so page height
          never shrinks on tab switch, preventing scroll clamping.
        */}
        <div className="grid">
          <TabsContent
            value="sea"
            forceMount
            className="col-start-1 row-start-1 data-[state=inactive]:invisible"
          >
            <Tabs defaultValue="hull">
              <TabsList>
                <TabsTrigger value="hull" disabled={!enabled}>
                  Hull
                </TabsTrigger>
              </TabsList>
              <TabsContent value="hull" />
            </Tabs>
          </TabsContent>

          <TabsContent
            value="air"
            forceMount
            className="col-start-1 row-start-1 data-[state=inactive]:invisible"
          >
            {/* NASA SUPIN is desktop-only — its license gate (restrictions +
                signed agreement + access password) can't be surfaced in the
                mobile parameter sheet, so the inlet option is not offered on
                mobile. Only the (coming-soon) Airframe tab remains. */}
            <Tabs defaultValue="airframe">
              <TabsList>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <TabsTrigger value="airframe" disabled>
                        Airframe
                      </TabsTrigger>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Coming soon.</TooltipContent>
                </Tooltip>
              </TabsList>
              <TabsContent value="airframe" />
            </Tabs>
          </TabsContent>

          <TabsContent
            value="land"
            forceMount
            className="col-start-1 row-start-1 data-[state=inactive]:invisible"
          >
            <Tabs defaultValue="tubeframe-joint">
              <TabsList>
                <TabsTrigger value="spaceframe" disabled={!enabled}>
                  Spaceframe Tube Chassis
                </TabsTrigger>
                <TabsTrigger value="tubeframe-joint" disabled={!enabled}>
                  Tubeframe Joint
                </TabsTrigger>
              </TabsList>
              <TabsContent value="spaceframe">
                {/* The FIA driver-cell configurator is a desktop workbench (10
                    floating feature windows over the viewer). */}
                <p className="px-1 py-2 text-xs text-telekinesis-neutral-50">
                  Spaceframe Chassis is a desktop workbench — open Telekinesis
                  on a desktop browser to configure an FIA driver cell.
                </p>
              </TabsContent>
              <TabsContent value="tubeframe-joint">
                <TubeframeForm
                  enabled={enabled}
                  tubeUnits={tubeUnits}
                  setTubeUnits={setTubeUnits}
                  tubeOD={tubeOD}
                  setTubeOD={setTubeOD}
                  tubeID={tubeID}
                  setTubeID={setTubeID}
                  tubeJointAngle={tubeJointAngle}
                  setTubeJointAngle={setTubeJointAngle}
                  tubeMatingSurface={tubeMatingSurface}
                  setTubeMatingSurface={setTubeMatingSurface}
                />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent
            value="space"
            forceMount
            className="col-start-1 row-start-1 data-[state=inactive]:invisible"
          >
            <Tabs defaultValue="rocket-nozzle">
              <TabsList>
                <TabsTrigger value="rocket-nozzle" disabled={!enabled}>
                  Rocket Nozzle
                </TabsTrigger>
              </TabsList>
              <TabsContent value="rocket-nozzle">
                <RocketNozzleForm
                  enabled={enabled}
                  inputs={inputs}
                  setInput={setInput}
                  outputFields={outputFields}
                />
              </TabsContent>
            </Tabs>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
